import React, { useEffect, useState } from "react";
import styles from "./EntrepreneurAccountChat.module.css";
import InboxItem from "../../components/InboxChat/InboxChat";
import { useTranslation } from "react-i18next";
import api, { socketBaseUrl } from "../../utils/axios/api";
import Chat from "../../components/Chat/Chat";
import { io, Socket } from "socket.io-client";
import defaultAvatar from "../../assets/defaultAvatar.svg";

interface Mensagem {
  senderName: string;
  messagePreview: string;
  unreadCount: number;
  profileImage: string;
  data: Date;
  chatType: "PRODUCT" | "ORDER";
  chatId: string;
  otherUserId: string;
  id?: string;
}

interface ChatInfo {
  chatType: "PRODUCT" | "ORDER";
  chatId: string;
  senderName: string;
  otherUserId: string;
}

function EntrepreneurAccountChat() {
  const opcoesOrdenacao = ["Mais recentes", "Mais antigos", "A-Z", "Z-A"];
  const [ordenacaoSelecionada, setOrdenacaoSelecionada] = useState(
    opcoesOrdenacao[0]
  );
  const [conversas, setConversas] = useState<Mensagem[]>([]);
  const [conversasOrdenadas, setConversasOrdenadas] = useState<Mensagem[]>([]);
  const [chatAberto, setChatAberto] = useState<ChatInfo | null>(null);
  const { t } = useTranslation();

  const entrepreneurId = localStorage.getItem("userId"); // Supondo que o ID do empreendedor esteja armazenado aqui
  const [socket, setSocket] = useState<Socket | null>(null);

  // Conectar ao Socket.IO
  useEffect(() => {
    if (entrepreneurId) {
      const newSocket = io(socketBaseUrl); // Substitua pela URL do seu servidor
      setSocket(newSocket);

      // Emitir evento de login com o ID do empreendedor
      newSocket.emit("login", entrepreneurId);

      // Listener para mensagens recebidas
      newSocket.on("chat message", (message: any) => {
        // Verificar se a mensagem pertence ao empreendedor
        console.log("Nova mensagem recebida:", message);
        if (
          message.receiverUserId === entrepreneurId ||
          message.senderUserId === entrepreneurId
        ) {
          setConversas((prevConversas) => {
            // Identificar o chatId baseado no chatType
            const currentChatId =
              message.chatType === "PRODUCT"
                ? message.productId
                : message.orderId;
            const conversaIndex = prevConversas.findIndex(
              (c) => c.chatId === currentChatId
            );

            if (conversaIndex !== -1) {
              // Atualizar a conversa existente
              const updatedConversas = [...prevConversas];
              updatedConversas[conversaIndex].messagePreview = message.message;
              updatedConversas[conversaIndex].data = new Date(
                message.createdAt
              );

              // Incrementar unreadCount apenas se a mensagem for do remetente
              if (message.senderUserId !== entrepreneurId) {
                updatedConversas[conversaIndex].unreadCount += 1;
              }

              return updatedConversas;
            } else {
              // Adicionar nova conversa
              return [
                ...prevConversas,
                {
                  senderName:
                    message.senderUserId === entrepreneurId
                      ? "Você"
                      : message.senderName,
                  messagePreview: message.message,
                  unreadCount: message.senderUserId !== entrepreneurId ? 1 : 0,
                  profileImage:
                    message.senderUserId !== entrepreneurId
                      ? message.senderImage || defaultAvatar
                      : "",
                  data: new Date(message.createdAt),
                  chatType: message.chatType,
                  chatId: currentChatId,
                  otherUserId:
                    message.senderUserId === entrepreneurId
                      ? message.receiverUserId
                      : message.senderUserId,
                },
              ];
            }
          });
        }
      });

      // Listener para erros do Socket.IO
      newSocket.on("error", (error: any) => {
        console.error("Erro no Socket.IO:", error);
      });

      // Cleanup na desconexão
      return () => {
        newSocket.disconnect();
      };
    }
  }, [entrepreneurId]);

  // Buscar conversas do backend
  useEffect(() => {
    async function loadChats() {
      if (entrepreneurId) {
        try {
          const response = await api.get(`/chat/user/${entrepreneurId}`);
          const conversasComTipos: Mensagem[] = response.data.map(
            (conversa: any) => ({
              senderName: conversa.senderName,
              messagePreview: conversa.messagePreview,
              unreadCount: conversa.unreadCount,
              profileImage: conversa.profileImage,
              data: new Date(conversa.data),
              chatType: conversa.chatType,
              chatId: conversa.chatId,
              otherUserId: conversa.otherUserId,
              id: conversa.id,
            })
          );

          console.log("Conversas carregadas:", conversasComTipos);
          setConversas(conversasComTipos);
          ordenarConversas(ordenacaoSelecionada, conversasComTipos);
        } catch (error) {
          console.error("Erro ao carregar conversas:", error);
        }
      }
    }
    loadChats();
  }, [entrepreneurId]);

  // Ordenar conversas sempre que houver mudança na ordenação ou nas conversas
  useEffect(() => {
    ordenarConversas(ordenacaoSelecionada, conversas);
  }, [ordenacaoSelecionada, conversas]);

  const handleOrdenacaoChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setOrdenacaoSelecionada(event.target.value);
  };

  const ordenarConversas = (
    opcao: string,
    conversasToSort: Mensagem[] = conversas
  ) => {
    const conversasCopia = [...conversasToSort];
    switch (opcao) {
      case "Mais recentes":
        conversasCopia.sort((a, b) => b.data.getTime() - a.data.getTime());
        break;
      case "Mais antigos":
        conversasCopia.sort((a, b) => a.data.getTime() - b.data.getTime());
        break;
      case "A-Z":
        conversasCopia.sort((a, b) => a.senderName.localeCompare(b.senderName));
        break;
      case "Z-A":
        conversasCopia.sort((a, b) => b.senderName.localeCompare(a.senderName));
        break;
      default:
        break;
    }
    setConversasOrdenadas(conversasCopia);
  };

  const handleChatClick = (conversa: Mensagem) => {
    setChatAberto({
      chatType: conversa.chatType,
      chatId: conversa.chatId,
      senderName: conversa.senderName,
      otherUserId: conversa.otherUserId,
    });
    // Chamar API para marcar como lidas
    if (entrepreneurId && conversa.chatId) {
      api
        .post("/chat/markAsRead", {
          userId: entrepreneurId,
          chatId: conversa.id,
        })
        .then(() => {
          // Atualizar o estado local para refletir que as mensagens foram lidas
          setConversas((prevConversas) =>
            prevConversas.map((c) =>
              c.chatId === conversa.chatId ? { ...c, unreadCount: 0 } : c
            )
          );
        })
        .catch((error) => {
          console.error("Erro ao marcar mensagens como lidas:", error);
        });
    }
  };

  const handleCloseChat = () => {
    setChatAberto(null);
  };

  useEffect(() => {
    if (chatAberto && socket) {
      socket.on("chat message", (message: any) => {
        if (
          message.receiverUserId === entrepreneurId ||
          message.senderUserId === entrepreneurId
        ) {
          const currentChatId =
            message.chatType === "PRODUCT"
              ? message.productId
              : message.orderId;

          setConversas((prevConversas) => {
            const conversaIndex = prevConversas.findIndex(
              (c) => c.chatId === currentChatId
            );

            if (conversaIndex !== -1) {
              const updatedConversas = [...prevConversas];
              updatedConversas[conversaIndex].messagePreview = message.message;
              updatedConversas[conversaIndex].data = new Date(
                message.createdAt
              );

              // Verificar se o chat está aberto para zerar o contador de não lidas
              if (chatAberto && chatAberto.chatId === currentChatId) {
                updatedConversas[conversaIndex].unreadCount = 0;
              } else if (message.senderUserId !== entrepreneurId) {
                // Incrementar contador de não lidas apenas se não for o remetente
                updatedConversas[conversaIndex].unreadCount += 1;
              }

              return updatedConversas;
            } else {
              return [
                ...prevConversas,
                {
                  senderName:
                    message.senderUserId === entrepreneurId
                      ? "Você"
                      : message.senderName,
                  messagePreview: message.message,
                  unreadCount:
                    chatAberto && chatAberto.chatId === currentChatId
                      ? 0
                      : message.senderUserId !== entrepreneurId
                      ? 1
                      : 0,
                  profileImage:
                    message.senderUserId !== entrepreneurId
                      ? message.senderImage || ""
                      : "",
                  data: new Date(message.createdAt),
                  chatType: message.chatType,
                  chatId: currentChatId,
                  otherUserId:
                    message.senderUserId === entrepreneurId
                      ? message.receiverUserId
                      : message.senderUserId,
                },
              ];
            }
          });
        }
      });
    }
  }, [chatAberto, socket]);

  return (
    <div>
      <main className={styles.visualizarchat}>
        <section className={styles.content}>
          <div className={styles.firstLine}>
            <h1 className={styles.titulo}>{t("visualizarConversa.chat")}</h1>
            <div className="custom-select"></div>
            <select
              value={ordenacaoSelecionada}
              onChange={handleOrdenacaoChange}
            >
              {opcoesOrdenacao.map((opcao, index) => (
                <option key={index} value={opcao}>
                  {opcao}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.chat}>
            {conversasOrdenadas.map((conversa, index) => (
              <InboxItem
                key={index}
                senderName={conversa.senderName}
                messagePreview={conversa.messagePreview}
                unreadCount={conversa.unreadCount}
                profileImage={conversa.profileImage}
                date={`${conversa.data.getDate()}/${
                  conversa.data.getMonth() + 1
                }/${conversa.data.getFullYear()}`}
                onClick={() => handleChatClick(conversa)}
              />
            ))}
          </div>
        </section>
      </main>

      {/* Renderizar o Chat diretamente na página */}
      {chatAberto && socket && (
        <Chat
          socket={socket}
          type={chatAberto.chatType}
          id={chatAberto.chatId}
          onClose={handleCloseChat}
          infos={{
            otherUserId: chatAberto.otherUserId, // Use o ID correto do outro usuário
            userName: chatAberto.senderName,
          }}
        />
      )}
    </div>
  );
}

export default EntrepreneurAccountChat;
